import { useState } from "react";
import { DbNewsItem } from "../../../lib/supabase";
import { LANGUAGES, LangData, DEFAULT_LANG_DATA } from "../../../lib/newsUtils";
import ImageUploader from "./ImageUploader";
import LangFields from "./LangFields";

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: "grant-call", label: "Grant Call / Open Call" },
  { value: "news", label: "News" },
  { value: "event", label: "Event" },
  { value: "result", label: "Project Result" },
];

const CATEGORY_DEFAULT_LABELS: Record<string, string> = {
  "grant-call": "Open Call",
  news: "News",
  event: "Event",
  result: "Project Result",
};

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

// ─── Props ────────────────────────────────────────────────────────────────────
interface FormShared {
  slug: string;
  category: string;
  date: string;
  deadline: string;
  source: string;
  image: string;
  status: "draft" | "published";
}

export interface NewsFormProps {
  initial?: DbNewsItem | null;
  initialTranslations?: Record<string, LangData>;
  initialIsAITranslated?: Record<string, boolean>;
  onSubmit: (
    main: Omit<DbNewsItem, "id" | "created_at" | "updated_at">,
    translations: Record<string, LangData>,
    isAITranslated: Record<string, boolean>
  ) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function NewsForm({
  initial,
  initialTranslations = {},
  initialIsAITranslated = {},
  onSubmit,
  onCancel,
  loading,
}: NewsFormProps) {
  const isEdit = !!initial;
  const [activeLang, setActiveLang] = useState<string>("en");
  const [slugManual, setSlugManual] = useState(isEdit);

  // AI Translation state
  const [translatePanelOpen, setTranslatePanelOpen] = useState(false);
  const [translateSource, setTranslateSource] = useState<string>("en");
  const [translateTargets, setTranslateTargets] = useState<string[]>([]);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [translateSuccess, setTranslateSuccess] = useState<string | null>(null);
  const [translateError, setTranslateError] = useState<string | null>(null);

  // Track which languages were AI translated
  const [isAITranslated, setIsAITranslated] = useState<Record<string, boolean>>(initialIsAITranslated);

  const [shared, setShared] = useState<FormShared>({
    slug: initial?.slug ?? "",
    category: initial?.category ?? "news",
    date: initial?.date ?? new Date().toISOString().split("T")[0],
    deadline: initial?.deadline ?? "",
    source: initial?.source ?? "",
    image: initial?.image ?? "",
    status: initial?.status ?? "draft",
  });

  const [langs, setLangs] = useState<Record<string, LangData>>({
    en: {
      title: initial?.title ?? "",
      excerpt: initial?.excerpt ?? "",
      body: initial?.body ?? "",
      tags: initial?.tags ?? [],
      category_label: initial?.category_label ?? "",
    },
    ro: initialTranslations.ro ?? { ...DEFAULT_LANG_DATA },
    ru: initialTranslations.ru ?? { ...DEFAULT_LANG_DATA },
  });

  const setSharedField = (key: keyof FormShared, value: string) =>
    setShared((prev) => ({ ...prev, [key]: value }));

  const updateLang = (lang: string, updated: LangData) => {
    setLangs((prev) => ({ ...prev, [lang]: updated }));
  };

  const handleEnChange = (updated: LangData) => {
    if (!slugManual && updated.title !== langs.en.title) {
      setShared((prev) => ({ ...prev, slug: generateSlug(updated.title) }));
    }
    updateLang("en", updated);
  };

  const handleCategoryChange = (value: string) => {
    setSharedField("category", value);
    const defaultLabel = CATEGORY_DEFAULT_LABELS[value] || value;
    const prevDefault = CATEGORY_DEFAULT_LABELS[shared.category] || shared.category;
    if (!langs.en.category_label || langs.en.category_label === prevDefault) {
      setLangs((prev) => ({
        ...prev,
        en: { ...prev.en, category_label: defaultLabel },
      }));
    }
  };

  // ─── AI Translation ──────────────────────────────────────────────────────────
  const hasLangContent = (code: string) => {
    const d = langs[code];
    if (!d) return false;
    return !!(d.title.trim() || d.excerpt.trim() || d.body.replace(/<[^>]*>/g, "").trim());
  };

  const sourcesWithContent = LANGUAGES.filter((l) => hasLangContent(l.code));

  const handleAITranslate = async () => {
    setTranslateError(null);
    setTranslateSuccess(null);
    setTranslateLoading(true);

    const targetLangs = translateTargets.filter((c) => c !== translateSource);
    const content = langs[translateSource];

    if (targetLangs.length === 0) {
      setTranslateError("Please select at least one target language.");
      setTranslateLoading(false);
      return;
    }

    if (!content || !hasLangContent(translateSource)) {
      setTranslateError("The selected source language has no content to translate.");
      setTranslateLoading(false);
      return;
    }

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/translate-news`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ sourceLang: translateSource, targetLangs, content }),
      });

      const json = await res.json();

      if (!res.ok || json.error) {
        setTranslateError(json.error || "Translation failed. Please try again.");
        setTranslateLoading(false);
        return;
      }

      const { translations } = json as { translations: Record<string, LangData> };

      setLangs((prev) => {
        const updated = { ...prev };
        for (const lang of targetLangs) {
          if (translations[lang]) {
            updated[lang] = {
              title: translations[lang].title || "",
              excerpt: translations[lang].excerpt || "",
              body: translations[lang].body || "",
              tags: translations[lang].tags || [],
              category_label: translations[lang].category_label || "",
            };
          }
        }
        return updated;
      });

      // Mark AI-translated languages
      setIsAITranslated((prev) => {
        const updated = { ...prev };
        for (const lang of targetLangs) {
          updated[lang] = true;
        }
        return updated;
      });

      const targetNames = targetLangs
        .map((l) => LANGUAGES.find((x) => x.code === l)?.label)
        .filter(Boolean)
        .join(", ");
      setTranslateSuccess(`Successfully translated to ${targetNames}!`);
      setTranslatePanelOpen(false);

      // Auto-switch to first translated tab
      setActiveLang(targetLangs[0]);
    } catch (err) {
      setTranslateError("Network error. Please check your connection.");
    } finally {
      setTranslateLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const enData = langs.en;

    const main: Omit<DbNewsItem, "id" | "created_at" | "updated_at"> = {
      slug: shared.slug,
      category: shared.category as DbNewsItem["category"],
      category_label: enData.category_label,
      title: enData.title,
      excerpt: enData.excerpt,
      body: enData.body,
      date: shared.date,
      deadline: shared.deadline || null,
      source: shared.source || null,
      image: shared.image,
      tags: enData.tags.length > 0 ? enData.tags : null,
      status: shared.status,
    };

    const translations: Record<string, LangData> = {};
    const aiFlags: Record<string, boolean> = {};
    for (const [lang, data] of Object.entries(langs)) {
      if (lang === "en") continue;
      translations[lang] = data;
      aiFlags[lang] = isAITranslated[lang] ?? false;
    }

    await onSubmit(main, translations, aiFlags);
  };

  const labelClass =
    "block text-xs font-semibold text-navy/60 uppercase tracking-wider mb-1.5";
  const inputClass =
    "w-full px-3.5 py-2.5 text-sm text-navy bg-cool-gray-50 border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors";

  const hasRoContent = hasLangContent("ro");
  const hasRuContent = hasLangContent("ru");

  const translationSuffix = [
    hasRoContent ? "RO" : "",
    hasRuContent ? "RU" : "",
  ].filter(Boolean).join(" + ");
  const saveSuffix = translationSuffix ? ` + ${translationSuffix}` : "";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* ── Status toggle ─────────────────────────────────────── */}
      <div className="flex items-center justify-between p-4 bg-cool-gray-50 rounded-md border border-cool-gray-200">
        <div>
          <p className="text-sm font-semibold text-navy">
            {shared.status === "published" ? "Published" : "Draft"}
          </p>
          <p className="text-xs text-navy/45 mt-0.5">
            {shared.status === "published"
              ? "Visible to all site visitors"
              : "Hidden from public — save as draft to finish later"}
          </p>
        </div>
        <button
          type="button"
          onClick={() =>
            setSharedField(
              "status",
              shared.status === "published" ? "draft" : "published"
            )
          }
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 cursor-pointer focus:outline-none ${
            shared.status === "published" ? "bg-emerald-500" : "bg-cool-gray-300"
          }`}
          aria-pressed={shared.status === "published"}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
              shared.status === "published" ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* ── Slug + Category ───────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>
            Slug *{" "}
            <span className="text-navy/30 font-normal normal-case tracking-normal">
              (used in URL)
            </span>
          </label>
          <input
            type="text"
            value={shared.slug}
            onChange={(e) => {
              setSlugManual(true);
              setSharedField("slug", e.target.value);
            }}
            required
            placeholder="article-url-slug"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Category *</label>
          <select
            value={shared.category}
            onChange={(e) => handleCategoryChange(e.target.value)}
            required
            className={inputClass}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Date + Deadline ───────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Publication Date *</label>
          <input
            type="date"
            value={shared.date}
            onChange={(e) => setSharedField("date", e.target.value)}
            required
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>
            Deadline{" "}
            <span className="text-navy/30 font-normal normal-case tracking-normal">
              (optional)
            </span>
          </label>
          <input
            type="date"
            value={shared.deadline}
            onChange={(e) => setSharedField("deadline", e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      {/* ── Source ────────────────────────────────────────────── */}
      <div>
        <label className={labelClass}>
          Source{" "}
          <span className="text-navy/30 font-normal normal-case tracking-normal">
            (optional)
          </span>
        </label>
        <input
          type="text"
          value={shared.source}
          onChange={(e) => setSharedField("source", e.target.value)}
          placeholder="e.g. EU Delegation to Moldova"
          className={inputClass}
        />
      </div>

      {/* ── Cover Image ───────────────────────────────────────── */}
      <div>
        <label className={labelClass}>Cover Image *</label>
        <ImageUploader
          value={shared.image}
          onChange={(url) => setSharedField("image", url)}
          required
        />
      </div>

      {/* ── Language tabs ─────────────────────────────────────── */}
      <div className="border border-cool-gray-200 rounded-lg overflow-visible">
        {/* Tab bar */}
        <div className="flex items-stretch border-b border-cool-gray-200 bg-cool-gray-50">
          <div className="flex flex-1">
            {LANGUAGES.map((lang) => {
              const isActive = activeLang === lang.code;
              const hasContent = lang.code !== "en" && hasLangContent(lang.code);
              const isAI = lang.code !== "en" && !!isAITranslated[lang.code];
              return (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setActiveLang(lang.code)}
                  className={`relative flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "bg-white text-navy border-b-2 border-navy -mb-px"
                      : "text-navy/45 hover:text-navy hover:bg-cool-gray-100"
                  }`}
                >
                  <span className="text-xs font-bold tracking-widest opacity-60">
                    {lang.flag}
                  </span>
                  {lang.label}
                  {lang.code === "en" && (
                    <span className="text-xs text-navy/30 font-normal">(default)</span>
                  )}
                  {/* AI dot — violet sparkle */}
                  {isAI && (
                    <span
                      title="AI translated"
                      className="w-3.5 h-3.5 flex items-center justify-center rounded-full bg-violet-100 text-violet-600 absolute top-2 right-1.5"
                    >
                      <i className="ri-sparkling-2-fill text-[8px]" />
                    </span>
                  )}
                  {/* Manual content dot — emerald */}
                  {hasContent && !isAI && (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 absolute top-2.5 right-2" />
                  )}
                </button>
              );
            })}
          </div>

          {/* AI Translation button */}
          <div className="flex items-center px-3 border-l border-cool-gray-200">
            <button
              type="button"
              onClick={() => {
                const opening = !translatePanelOpen;
                setTranslatePanelOpen(opening);
                setTranslateError(null);
                setTranslateSuccess(null);
                if (opening) {
                  const defaultSource =
                    sourcesWithContent.length > 0 && hasLangContent(translateSource)
                      ? translateSource
                      : sourcesWithContent[0]?.code ?? "en";
                  setTranslateSource(defaultSource);
                  // Default targets: all langs without content (excluding source)
                  const defaultTargets = LANGUAGES.map((l) => l.code).filter(
                    (c) => c !== defaultSource && !hasLangContent(c)
                  );
                  setTranslateTargets(defaultTargets.length > 0 ? defaultTargets : LANGUAGES.map((l) => l.code).filter((c) => c !== defaultSource));
                }
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                translatePanelOpen
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy border-cool-gray-200 hover:border-navy hover:bg-navy/5"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-sparkling-2-line text-sm" />
              </span>
              AI Translation
            </button>
          </div>
        </div>

        {/* ── AI Translation Panel ─────────────────────────────── */}
        {translatePanelOpen && (
          <div className="px-6 py-5 bg-gradient-to-r from-violet-50 to-indigo-50 border-b border-violet-100">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-sparkling-2-fill text-violet-600 text-base" />
              </span>
              <span className="text-sm font-semibold text-violet-900">AI Translation</span>
              <span className="text-xs text-violet-400 font-normal">— select source and target languages</span>
            </div>

            {/* From → To selector */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* FROM */}
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-bold text-violet-500 uppercase tracking-widest">From</span>
                <div className="flex gap-1.5">
                  {LANGUAGES.map((lang) => {
                    const hasContent = hasLangContent(lang.code);
                    const isSelected = translateSource === lang.code;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        disabled={!hasContent}
                        onClick={() => {
                          setTranslateSource(lang.code);
                          setTranslateTargets((prev) =>
                            prev.filter((c) => c !== lang.code)
                          );
                        }}
                        title={!hasContent ? `${lang.label} — no content yet` : undefined}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border transition-all cursor-pointer whitespace-nowrap ${
                          isSelected && hasContent
                            ? "bg-violet-600 text-white border-violet-600 shadow-sm"
                            : hasContent
                            ? "bg-white text-violet-700 border-violet-200 hover:border-violet-400 hover:bg-violet-50"
                            : "bg-white/40 text-navy/25 border-cool-gray-200 cursor-not-allowed"
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.label}</span>
                        {hasContent && (
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white/70" : "bg-emerald-400"}`}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Arrow */}
              <div className="flex flex-col items-center gap-1.5 pt-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-violet-100 text-violet-500">
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </div>

              {/* TO */}
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-bold text-violet-500 uppercase tracking-widest">To</span>
                <div className="flex gap-1.5">
                  {LANGUAGES.filter((l) => l.code !== translateSource).map((lang) => {
                    const isSelected = translateTargets.includes(lang.code);
                    const hasExisting = hasLangContent(lang.code);
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() =>
                          setTranslateTargets((prev) =>
                            prev.includes(lang.code)
                              ? prev.filter((c) => c !== lang.code)
                              : [...prev, lang.code]
                          )
                        }
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border transition-all cursor-pointer whitespace-nowrap ${
                          isSelected
                            ? "bg-violet-600 text-white border-violet-600 shadow-sm"
                            : "bg-white text-violet-700 border-violet-200 hover:border-violet-400 hover:bg-violet-50"
                        }`}
                      >
                        <span
                          className={`w-3.5 h-3.5 flex items-center justify-center rounded border transition-colors ${
                            isSelected
                              ? "bg-white/20 border-white/40"
                              : "border-violet-300"
                          }`}
                        >
                          {isSelected && <i className="ri-check-line text-[9px]" />}
                        </span>
                        <span>{lang.flag}</span>
                        <span>{lang.label}</span>
                        {hasExisting && (
                          <span
                            title="Already has content — will be overwritten"
                            className={`text-[9px] font-bold uppercase tracking-wide ${isSelected ? "text-white/60" : "text-amber-500"}`}
                          >
                            ↺
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {translateTargets.some((c) => hasLangContent(c)) && (
                  <p className="text-[10px] text-amber-600 mt-0.5">
                    <i className="ri-alert-line mr-0.5" />
                    Existing content in selected languages will be overwritten.
                  </p>
                )}
              </div>

              {/* Translate button */}
              <div className="flex flex-col gap-1.5 pt-4">
                <button
                  type="button"
                  onClick={handleAITranslate}
                  disabled={translateLoading || !hasLangContent(translateSource) || translateTargets.length === 0}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-violet-600 text-white text-xs font-semibold rounded-md hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  {translateLoading ? (
                    <>
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin" />
                      </span>
                      Translating…
                    </>
                  ) : (
                    <>
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-translate-2" />
                      </span>
                      Translate
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Summary line */}
            {hasLangContent(translateSource) && translateTargets.length > 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-violet-700 bg-white/60 border border-violet-100 rounded-md px-3 py-2">
                <i className="ri-information-line text-violet-400" />
                <span>
                  Will translate{" "}
                  <strong>{LANGUAGES.find((l) => l.code === translateSource)?.label}</strong>{" "}
                  →{" "}
                  <strong>
                    {translateTargets
                      .map((c) => LANGUAGES.find((l) => l.code === c)?.label)
                      .filter(Boolean)
                      .join(" + ")}
                  </strong>
                </span>
              </div>
            )}

            {/* Error message */}
            {translateError && (
              <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-500" />
                </span>
                {translateError}
                {translateError.includes("OPENAI_API_KEY") && (
                  <span className="ml-1 font-semibold">
                    — Add OPENAI_API_KEY to your Supabase project secrets.
                  </span>
                )}
              </div>
            )}

            {!hasLangContent(translateSource) && !translateLoading && (
              <p className="mt-2 text-xs text-violet-500">
                <i className="ri-information-line mr-1" />
                The selected source language has no content yet. Fill in at least the title and excerpt first.
              </p>
            )}
          </div>
        )}

        {/* Translation success banner */}
        {translateSuccess && (
          <div className="px-6 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-emerald-500" />
            </span>
            <p className="text-xs text-emerald-700 font-medium">{translateSuccess}</p>
            <button
              type="button"
              onClick={() => setTranslateSuccess(null)}
              className="ml-auto w-4 h-4 flex items-center justify-center text-emerald-400 hover:text-emerald-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        )}

        {/* Tab hint for non-default */}
        {activeLang !== "en" && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
            <i className="ri-translate-2 text-amber-600 text-sm" />
            <p className="text-xs text-amber-700">
              All fields in this tab are optional. If left empty, the English version will be shown to {activeLang === "ro" ? "Romanian" : "Russian"} visitors.
            </p>
          </div>
        )}

        {/* AI translation flag banner */}
        {activeLang !== "en" && isAITranslated[activeLang] && (
          <div className="px-6 py-3 bg-violet-50 border-b border-violet-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-violet-100">
                <i className="ri-sparkling-2-fill text-violet-600 text-[10px]" />
              </span>
              <p className="text-xs text-violet-800 font-medium">
                This content was generated by AI translation.
              </p>
              <span className="text-xs text-violet-500">
                Review it and mark as verified when done.
              </span>
            </div>
            <button
              type="button"
              onClick={() =>
                setIsAITranslated((prev) => ({ ...prev, [activeLang]: false }))
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-violet-700 bg-white border border-violet-200 rounded-md hover:bg-violet-700 hover:text-white hover:border-violet-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-check-double-line" />
              </span>
              Mark as manually reviewed
            </button>
          </div>
        )}

        {/* Tab content */}
        <div className="p-6">
          <LangFields
            key={activeLang}
            langCode={activeLang}
            data={langs[activeLang] ?? DEFAULT_LANG_DATA}
            onChange={activeLang === "en" ? handleEnChange : (u) => updateLang(activeLang, u)}
            isDefault={activeLang === "en"}
          />
        </div>
      </div>

      {/* Translation status bar */}
      <div className="flex items-center gap-3 px-4 py-3 bg-cool-gray-50 rounded-md border border-cool-gray-100">
        <i className="ri-global-line text-navy/30 text-sm" />
        <div className="flex items-center gap-3 flex-wrap">
          {LANGUAGES.map((lang) => {
            const filled =
              lang.code === "en"
                ? !!(langs.en.title && langs.en.excerpt)
                : hasLangContent(lang.code);
            const isAI = lang.code !== "en" && !!isAITranslated[lang.code];
            return (
              <span
                key={lang.code}
                className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                  filled
                    ? isAI
                      ? "text-violet-700"
                      : "text-emerald-700"
                    : "text-navy/35"
                }`}
              >
                {filled ? (
                  isAI ? (
                    <i className="ri-sparkling-2-fill text-violet-500 text-sm" />
                  ) : (
                    <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />
                  )
                ) : (
                  <i className="ri-checkbox-blank-circle-line text-sm" />
                )}
                {lang.label}
                {lang.code === "en" && (
                  <span className="text-navy/25 font-normal">required</span>
                )}
                {isAI && (
                  <span className="text-[9px] font-bold text-violet-400 uppercase tracking-wide">AI</span>
                )}
              </span>
            );
          })}
        </div>
      </div>

      {/* ── Actions ───────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-cool-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2.5 text-sm font-semibold text-navy/60 hover:text-navy border border-cool-gray-200 rounded-md transition-colors cursor-pointer whitespace-nowrap"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-navy text-white text-sm font-semibold rounded-md hover:bg-navy/90 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
        >
          {loading ? (
            <>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin" />
              </span>
              Saving…
            </>
          ) : (
            <>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={isEdit ? "ri-save-line" : "ri-add-line"} />
              </span>
              {isEdit
                ? `Save Changes${saveSuffix}`
                : `Save Article${saveSuffix}`}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
